import React, { useMemo, type MouseEventHandler } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { borderRadius } from '@atlassian/jira-common-styles/src';
import { singleLineClamp } from '@atlassian/jira-common-styles/src/mixins';
import { useIntl } from '@atlassian/jira-intl';
import { asColor, getLozengeThemeByColor } from '@atlassian/jira-issue-epic-color';
import { Link } from '@atlassian/jira-router';

import messages from './messages';

type Props = {
	issueKey: string;
	label: string;
	colorKey?: string | null;
	disableClickAction?: boolean;
	onClick?: MouseEventHandler;
};

export const Summary = ({ disableClickAction, onClick, label, colorKey, issueKey }: Props) => {
	const { formatMessage } = useIntl();

	const lozengeTheme = useMemo(
		() => (colorKey ? getLozengeThemeByColor(asColor(colorKey)) : undefined),
		[colorKey],
	);

	if (disableClickAction) {
		return (
			<WrapperNoClick
				// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={lozengeTheme}
			>
				{label}
			</WrapperNoClick>
		);
	}

	if (onClick != null) {
		return (
			<ButtonText
				onClick={onClick}
				// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={lozengeTheme}
			>
				{label}
			</ButtonText>
		);
	}

	return (
		<LinkText
			data-testid="issue-field-parent-readview-full.ui.parent.summary.view-link"
			href={`/browse/${issueKey}`}
			aria-label={formatMessage(messages.parentTitle, { label })}
			// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={lozengeTheme}
		>
			{label}
		</LinkText>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const LinkText = styled(Link)(singleLineClamp, {
	cursor: 'pointer',
	lineHeight: '16px',
	height: '16px',
	maxWidth: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
	padding: `${token('space.025', '2px')} ${token('space.050', '4px')}`,
	fontSize: '14px',
	'&:hover': {
		textDecoration: 'underline',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const ButtonText = styled.button(singleLineClamp, {
	// Disable buttons styles
	border: 'none',
	background: 'none',
	color: 'inherit',
	font: 'inherit',
	outline: 'inherit',

	// Add custom styles
	cursor: 'pointer',
	height: '20px',
	maxWidth: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
	padding: `${token('space.025', '2px')} ${token('space.050', '4px')}`,
	fontSize: '14px',
	lineHeight: '16px',
	'&:hover': {
		textDecoration: 'underline',
	},
	'&:focus': {
		outline: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const WrapperNoClick = styled.span(singleLineClamp, {
	display: 'block',
	lineHeight: '16px',
	height: '16px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
	padding: `${token('space.025', '2px')} ${token('space.050', '4px')}`,
	fontSize: '14px',
	maxWidth: '100%',
});
